/**
 * 动画工具函数
 */

import { newRegisterGuideStore } from "./hooks/useNewRegisterGuide";

interface Position {
  x: number;
  y: number;
}

interface BaseAnimationOptions {
  /** 动画时长（毫秒） */
  duration?: number;
  /** 缓动函数 */
  easing?: string;
}

interface ScaleOpacityOptions extends BaseAnimationOptions {
  /** 起始缩放值 */
  startScale?: number;
  /** 结束缩放值 */
  endScale?: number;
  /** 起始透明度 */
  startOpacity?: number;
  /** 结束透明度 */
  endOpacity?: number;
}

interface MoveOptions extends BaseAnimationOptions {
  /** 移动距离 X */
  deltaX?: number;
  /** 移动距离 Y */
  deltaY?: number;
}

interface CircularContainerConfig {
  /** 容器尺寸，默认64px */
  containerSize?: number;
  /** 图片尺寸，默认32px */
  imageSize?: number;
  /** 图片圆角，默认8px */
  borderRadius?: number;
  /** 白色边框宽度，默认2px */
  borderWidth?: number;
}

let animationIdCounter = 0;

/**
 * 通用CSS动画执行函数
 * @param element 目标元素
 * @param keyframes CSS keyframes 字符串
 * @param animationId 动画ID
 * @param duration 时长
 * @param easing 缓动函数
 * @param originalStyles 需要恢复的原始样式
 * @returns Promise<void>
 */
function _executeAnimation(
  element: HTMLElement,
  keyframes: string,
  animationId: string,
  duration: number,
  easing: string,
  originalStyles: { transform?: string; opacity?: string } = {}
): Promise<void> {
  return new Promise<void>((resolve) => {
    // 创建并添加样式
    const styleElement = document.createElement("style");
    styleElement.textContent = keyframes;
    document.head.appendChild(styleElement);

    // 设置清理函数
    const handleAnimationEnd = () => {
      element.removeEventListener("animationend", handleAnimationEnd);
      element.style.animation = "";

      // 恢复原始样式
      if (originalStyles.transform !== undefined) {
        element.style.transform = originalStyles.transform;
      }
      if (originalStyles.opacity !== undefined) {
        element.style.opacity = originalStyles.opacity;
      }

      styleElement.remove();

      resolve();
    };

    element.addEventListener("animationend", handleAnimationEnd);
    element.style.animation = `${animationId} ${duration}ms ${easing} both`;
  });
}

/**
 * 获取元素在页面中的中心位置坐标
 */
export function getElementPosition(element: HTMLElement): Position {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

/**
 * 核心动画函数：缩放 + 透明度动画
 * @param element 目标元素
 * @param options 动画选项
 * @returns Promise<void>
 */
function _createScaleOpacityAnimation(
  element: HTMLElement,
  options: ScaleOpacityOptions = {}
): Promise<void> {
  const {
    duration = 240,
    easing = "cubic-bezier(0.25, 1, 0.25, 1)",
    startScale = 3.0,
    endScale = 1.0,
    startOpacity = 1,
    endOpacity = 0,
  } = options;

  const animationId = `scale-opacity-${++animationIdCounter}`;

  const keyframes = `
    @keyframes ${animationId} {
      0% {
        transform: scale(${startScale});
        opacity: ${startOpacity};
      }
      100% {
        transform: scale(${endScale});
        opacity: ${endOpacity};
      }
    }
  `;

  const originalTransform = element.style.transform;
  const originalOpacity = element.style.opacity;

  return _executeAnimation(element, keyframes, animationId, duration, easing, {
    transform: originalTransform,
    opacity: originalOpacity,
  });
}

/**
 * 核心动画函数：移动 + 缩放 + 透明度动画
 * @param element 目标元素
 * @param options 动画选项
 * @returns Promise<void>
 */
function _createMoveScaleOpacityAnimation(
  element: HTMLElement,
  options: ScaleOpacityOptions & MoveOptions = {}
): Promise<void> {
  const {
    duration = 1200,
    easing = "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    startScale = 1.0,
    endScale = 0.3,
    startOpacity = 1,
    endOpacity = 0,
    deltaX = 0,
    deltaY = 0,
  } = options;

  const animationId = `move-scale-opacity-${++animationIdCounter}`;

  const keyframes = `
    @keyframes ${animationId} {
      0% {
        transform: scale(${startScale});
        opacity: ${startOpacity};
      }
      100% {
        transform: translate(${deltaX}px, ${deltaY}px) scale(${endScale});
        opacity: ${endOpacity};
      }
    }
  `;

  const originalTransform = element.style.transform;
  const originalOpacity = element.style.opacity;

  return _executeAnimation(element, keyframes, animationId, duration, easing, {
    transform: originalTransform,
    opacity: originalOpacity,
  });
}

/**
 * 核心动画函数：缩放脉冲动画
 * @param element 目标元素
 * @param options 动画选项
 * @returns Promise<void>
 */
function _createScalePulseAnimation(
  element: HTMLElement,
  options: BaseAnimationOptions & { maxScale?: number } = {}
): Promise<void> {
  const {
    duration = 600,
    easing = "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    maxScale = 1.2,
  } = options;

  const animationId = `scale-pulse-${++animationIdCounter}`;

  const keyframes = `
    @keyframes ${animationId} {
      0% { transform: scale(1); }
      50% { transform: scale(${maxScale}); }
      100% { transform: scale(1); }
    }
  `;

  const originalTransform = element.style.transform;

  return _executeAnimation(element, keyframes, animationId, duration, easing, {
    transform: originalTransform,
  });
}

/**
 * 创建圆形容器元素
 * @param imageSrc 图片源地址
 * @param x 位置 X 坐标
 * @param y 位置 Y 坐标
 * @param config 容器配置
 * @returns 容器元素
 */
function _createCircularContainer(
  imageSrc: string,
  x: number,
  y: number,
  config: CircularContainerConfig = {}
): HTMLElement {
  const {
    containerSize = 64 * 3,
    imageSize = 32 * 3,
    borderRadius = 8 * 3,
    borderWidth = 2 * 3,
  } = config;

  // 创建容器
  const container = document.createElement("div");
  Object.assign(container.style, {
    position: "fixed",
    left: `${x}px`,
    top: `${y}px`,
    width: `${containerSize}px`,
    height: `${containerSize}px`,
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: "9999",
    pointerEvents: "none",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  });

  // 创建图片
  const image = document.createElement("img");
  image.src = imageSrc;
  image.alt = "";
  Object.assign(image.style, {
    width: `${imageSize}px`,
    height: `${imageSize}px`,
    borderRadius: `${borderRadius}px`,
    border: `${borderWidth}px solid #fff`,
    objectFit: "cover",
    display: "block",
  });

  container.appendChild(image);
  document.body.appendChild(container);

  return container;
}

/**
 * 圆形容器图片飞入动画
 * @param imageSrc 图片源地址
 * @param startX 起始 X 坐标
 * @param startY 起始 Y 坐标
 * @param endX 终点 X 坐标
 * @param endY 终点 Y 坐标
 * @param options 动画选项
 * @returns Promise<void>
 */
export function createCircularImageFlyAnimation(
  imageSrc: string,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  options: {
    phase1Duration?: number;
    phase2Duration?: number;
    maxScale?: number;
    minScale?: number;
    easing?: string;
  } & CircularContainerConfig = {}
): Promise<void> {
  const {
    phase1Duration = 240,
    phase2Duration = 240,
    maxScale,
    minScale,
    easing,
    ...containerConfig
  } = options;

  return new Promise<void>(async (resolve) => {
    // 创建圆形容器
    const container = _createCircularContainer(
      imageSrc,
      startX,
      startY,
      containerConfig
    );

    try {
      // 第一阶段：缩放放大 + 透明到不透明
      await _createScaleOpacityAnimation(container, {
        duration: phase1Duration,
        easing,
        startScale: 0.33,
        endScale: 1,
        startOpacity: 0,
        endOpacity: 1,
      });

      await new Promise((resolve) => setTimeout(resolve, 100));

      // 第二阶段：移动 + 缩放缩小 + 不透明到透明
      await _createMoveScaleOpacityAnimation(container, {
        duration: phase2Duration,
        easing,
        startScale: 1,
        endScale: 0.33,
        startOpacity: 1,
        endOpacity: 0,
        deltaX: endX - startX,
        deltaY: endY - startY,
      });

      // 清理容器
      container.remove();
      resolve();
    } catch (error) {
      container.remove();
      resolve();
    }
  });
}

/**
 * 从元素到元素的圆形容器动画
 * @param imageSrc 图片源地址
 * @param from 起始元素或位置
 * @param to 目标元素或位置
 * @param options 动画选项
 * @returns Promise<void>
 */
export function createCircularImageFlyAnimationFromTo(
  imageSrc: string,
  from: HTMLElement | Position,
  to: HTMLElement | Position,
  options: Parameters<typeof createCircularImageFlyAnimation>[5] = {}
): Promise<void> {
  const startPos = "x" in from ? from : getElementPosition(from);
  const endPos = "x" in to ? to : getElementPosition(to);

  return createCircularImageFlyAnimation(
    imageSrc,
    startPos.x,
    startPos.y,
    endPos.x,
    endPos.y,
    options
  );
}

/**
 * 元素缩放脉冲动画
 * @param target 目标元素或选择器
 * @param duration 动画时长，默认600ms
 * @param maxScale 最大缩放值，默认1.2
 * @param easing 缓动函数
 * @returns Promise<void>
 */
export async function createScalePulseAnimation(
  target: HTMLElement | string,
  duration: number = 600,
  maxScale: number = 1.2,
  easing: string = "cubic-bezier(0.25, 0.46, 0.45, 0.94)"
): Promise<void> {
  const element =
    typeof target === "string"
      ? (document.querySelector(target) as HTMLElement)
      : target;

  if (!element) {
    console.warn("Scale pulse animation target element not found");
    return Promise.resolve();
  }

  return _createScalePulseAnimation(element, {
    duration,
    maxScale,
    easing,
  });
}

/**
 * 访问过我的灵感空间
 */
export const MY_SPACE_VISITED_KEY = "my-space-visited";
export async function createPinSuccessAnimation(image: string): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }
  if (newRegisterGuideStore.get(MY_SPACE_VISITED_KEY)) {
    return;
  }
  // 个人头像 el
  const el: HTMLElement | null = document.querySelector("#avatar-container");
  if (!el) {
    return;
  }

  Promise.all([
    new Promise((resolve) => setTimeout(resolve, 600)).then(() =>
      createScalePulseAnimation(el, 200, 1.2)
    ),
    createCircularImageFlyAnimationFromTo(
      image,
      { x: window.innerWidth + 37 - 64 * 3, y: 76 },
      { x: window.innerWidth + 35 - 64 * 3, y: -46 }
    ),
  ]);
}
